/*
 * The Belgian Brewer: table option in the private hire enquiry card (sections/tbb-hire.liquid).
 */
/*
 * "What would you like to book?": switches the enquiry card between the private hire form and the
 * Epping table booking form. The table form is fetched from the Book a table page (Shopify section
 * rendering), so there is only one table form to keep up to date. Answers carry across when
 * switching. More than 12 people, or a day the café is closed to the public, points back to hire.
 */
const TBB_DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function setUpTableMode(root) {
  const modeBox = root.querySelector('[data-tbb-mode]');
  const slot = root.querySelector('[data-tbb-table-slot]');
  if (!modeBox || !slot) return;
  const hireForm = root.querySelector('form#enquire');
  const radios = [...modeBox.querySelectorAll('input[type="radio"]')];
  const hint = root.querySelector('[data-tbb-table-hint]');
  const swap = root.querySelector('[data-tbb-closed-swap]');
  const venueSelect = root.querySelector('[data-tbb-venue-select]');
  const tableVenue = modeBox.dataset.tableVenue || 'Epping';
  let mode = 'hire';
  let loading = null;
  let tableRoot = null;
  let cfg = null;

  const hf = (name) => hireForm?.elements['contact[' + name + ']'];
  const tf = (name) => tableRoot?.querySelector('[data-tbb-table-form]')?.elements[name];
  const fire = (el) => el && el.dispatchEvent(new Event('change', { bubbles: true }));
  const copyIfEmpty = (to, value) => {
    if (to && value && !String(to.value).trim()) to.value = value;
  };

  const isClosed = (value) => {
    if (!value || !cfg) return false;
    if (String(cfg.closedDates || '').includes(value)) return true;
    const h = (cfg.hours || {})[TBB_DAY_KEYS[new Date(value + 'T12:00:00').getDay()]] || {};
    return !(h.open && h.close);
  };

  const wireTable = () => {
    const date = tableRoot.querySelector('[data-tbb-date]');
    date?.addEventListener('change', () => {
      if (swap) swap.hidden = mode !== 'table' || !isClosed(date.value);
    });
    // "Ask about private hire" (more than 12 people) switches to the hire form instead of leaving the page
    tableRoot.querySelectorAll('.tbb-table__hire a').forEach((link) =>
      link.addEventListener('click', (event) => {
        event.preventDefault();
        setMode('hire', { atVenue: true, focusGuests: true });
      })
    );
  };

  const load = () => {
    if (loading) return loading;
    // The whole Book a table page is fetched (section IDs in page templates differ between themes)
    loading = fetch(String(slot.dataset.src || '').split('?')[0], { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.text();
      })
      .then(async (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('[data-tbb-table]');
        const card = source?.querySelector('.tbb-table__card');
        const config = source?.querySelector('[data-tbb-table-config]');
        if (!card || !config) throw new Error('no table form');
        tableRoot = document.createElement('div');
        tableRoot.className = 'tbb-hire__table';
        tableRoot.setAttribute('data-tbb-table', '');
        tableRoot.append(document.importNode(card, true), document.importNode(config, true));
        slot.replaceChildren(tableRoot);
        try {
          cfg = JSON.parse(config.textContent);
        } catch (e) {
          cfg = null;
        }
        // Running the table booking script sets up every table form on the page that isn't ready yet
        const src = slot.dataset.script;
        if (src) {
          await import(src);
          if (!tableRoot.dataset.tbbReady) await import(src + (src.includes('?') ? '&' : '?') + 'r=' + Date.now());
        }
        wireTable();
        return tableRoot;
      })
      .catch(() => {
        loading = null;
        return null;
      });
    return loading;
  };

  // Hire form -> table form
  const toTable = () => {
    if (!tableRoot) return;
    const guests = tableRoot.querySelector('[data-tbb-guests]');
    const n = Number(hf('Guests')?.value);
    if (guests && n) {
      const min = Number(cfg?.minGuests || 2);
      const max = Number(cfg?.maxGuests || 12);
      if (n > max) guests.value = 'more';
      else if (n >= min) guests.value = String(n);
      fire(guests);
    }
    const date = tableRoot.querySelector('[data-tbb-date]');
    const wanted = hf('Preferred date')?.value;
    if (date && wanted && !date.value) {
      date.value = wanted;
      fire(date);
    }
    copyIfEmpty(tf('name'), hf('name')?.value);
    copyIfEmpty(tf('phone'), hf('phone')?.value);
    copyIfEmpty(tf('email'), hf('email')?.value);
    copyIfEmpty(tf('notes'), hf('body')?.value);
  };

  // Table form -> hire form
  const toHire = (opts) => {
    if (!hireForm) return;
    const hasVenue = venueSelect && [...venueSelect.options].some((o) => o.value === tableVenue);
    if (hasVenue && (opts.atVenue || !venueSelect.value) && venueSelect.value !== tableVenue) {
      venueSelect.value = tableVenue;
      fire(venueSelect);
    }
    const guests = tableRoot?.querySelector('[data-tbb-guests]')?.value;
    if (guests && guests !== 'more') copyIfEmpty(hf('Guests'), guests);
    copyIfEmpty(hf('Preferred date'), tableRoot?.querySelector('[data-tbb-date]')?.value);
    copyIfEmpty(hf('name'), tf('name')?.value);
    copyIfEmpty(hf('phone'), tf('phone')?.value);
    copyIfEmpty(hf('email'), tf('email')?.value);
    copyIfEmpty(hf('body'), tf('notes')?.value);
    syncHint();
    if (opts.focusGuests) hf('Guests')?.focus();
  };

  const setMode = (next, opts = {}) => {
    radios.forEach((radio) => {
      radio.checked = radio.value === next;
    });
    if (opts.scroll) modeBox.scrollIntoView({ block: 'start', behavior: 'smooth' });
    if (next === mode) return;
    mode = next;
    const table = next === 'table';
    root.classList.toggle('is-table-mode', table);
    if (hireForm) hireForm.hidden = table;
    slot.hidden = !table;
    if (swap && !table) swap.hidden = true;
    root.querySelectorAll('.tbb-hire__enq-copy [data-tbb-for]').forEach((el) => {
      el.hidden = el.dataset.tbbFor !== next;
    });
    if (table) load().then((el) => el && toTable());
    else toHire(opts);
  };

  // In the hire form: a gentle pointer to the table form for small groups at the table venue
  const syncHint = () => {
    if (!hint) return;
    const n = Number(hf('Guests')?.value);
    hint.hidden = !(venueSelect?.value === tableVenue && n >= 2 && n <= Number(cfg?.maxGuests || 12));
  };

  radios.forEach((radio) => radio.addEventListener('change', () => radio.checked && setMode(radio.value)));
  venueSelect?.addEventListener('change', syncHint);
  hf('Guests')?.addEventListener('input', syncHint);
  hint?.querySelector('button')?.addEventListener('click', () => setMode('table'));
  swap?.querySelector('button')?.addEventListener('click', () => setMode('hire', { atVenue: true }));
  root.querySelectorAll('[data-tbb-space]').forEach((link) => link.addEventListener('click', () => setMode('hire')));
  document.querySelectorAll('a[href$="#book-table"]').forEach((link) =>
    link.addEventListener('click', (event) => {
      event.preventDefault();
      setMode('table', { scroll: true });
    })
  );

  const restored = radios.find((radio) => radio.checked)?.value;
  if (location.hash === '#book-table') setMode('table', { scroll: true });
  else if (restored === 'table') setMode('table');
  else {
    // Fetch the table form quietly once the page has settled, so switching is instant
    const later = window.requestIdleCallback ? (fn) => window.requestIdleCallback(fn) : (fn) => setTimeout(fn, 1500);
    later(() => load());
  }
}

const initTables = (root = document) =>
  root.querySelectorAll('[data-tbb-hire]').forEach((el) => {
    if (el.dataset.tbbTablesReady) return;
    el.dataset.tbbTablesReady = 'true';
    setUpTableMode(el);
  });
initTables();
document.addEventListener('shopify:section:load', (event) => initTables(event.target));
